import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { ArrowRight, LogOut, Pencil, QrCode } from 'lucide-react'
import { API_BASE, api } from '../lib/api'
import { useSession } from '../lib/session'
import { ROLE_NAMES } from '../lib/types'
import type { ParticipantProfile, SaveProfileResponse } from '../lib/types'
import { APP_BUILD, APP_LABEL } from '../lib/app-version'
import { resetSlots } from '../features/useSlots'
import { Masthead } from '../ui/Chrome'
import { Button } from '../ui/Button'
import { Sheet } from '../ui/Sheet'
import { ParticipantProfileForm } from './ParticipantProfileForm'

export function AccountRoute() {
  const { session, signOut, isStaff } = useSession()
  const navigate = useNavigate()
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [imgBroken, setImgBroken] = useState(false)
  const [editingProfile, setEditingProfile] = useState(false)
  const [profile, setProfile] = useState<ParticipantProfile | null>(null)
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileError, setProfileError] = useState<string | null>(null)
  /*
   * Loaded on arrival rather than on the first tap, because the Edit button has to
   * know whether the name is still editable before anyone presses it. The lock is the
   * server's (certificate issued, or the organiser froze names); this only greys.
   */
  useEffect(() => {
    if (!session || isStaff) return
    let cancelled = false
    api.getParticipantProfile(session.email, session.at)
      .then((result) => { if (!cancelled) setProfile(result) })
      .catch(() => { /* the tap path below reports it */ })
    return () => { cancelled = true }
  }, [session, isStaff])

  if (!session) return null

  const initials = session.name
    .split(/\s+/)
    .filter((w) => /^[A-Za-z]/.test(w))
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('')

  async function openProfileEditor() {
    if (profile) {
      setEditingProfile((open) => !open)
      return
    }
    setProfileLoading(true)
    setProfileError(null)
    try {
      const result = await api.getParticipantProfile(session!.email, session!.at)
      setProfile(result)
      setEditingProfile(true)
    } catch {
      setProfileError('Could not load your profile. Try again in a moment.')
    } finally {
      setProfileLoading(false)
    }
  }

  async function leave() {
    setBusy(true)
    await signOut()
    resetSlots()
  }

  return (
    /*
     * `account-page` exists so the label/value emphasis below can be inverted here
     * without touching VenueSettings, which shares `.row` and `.row__label`. Its one
     * row is a toggle whose label is the thing you scan for, so the demotion that is
     * right here would be wrong there.
     */
    <main className="page account-page">
      <Masthead kicker="Signed in" title="Account" />

      <section className="account__id">
        <div className="account__avatar" aria-hidden="true">
          {session.img && !imgBroken ? (
            <img
              src={
                session.img.startsWith('data:') || session.img.startsWith('http')
                  ? session.img
                  : `data:image/jpeg;base64,${session.img}`
              }
              alt=""
              /* A staff photo that has moved off the CDN shows initials, not a broken icon. */
              onError={() => setImgBroken(true)}
            />
          ) : (
            initials || '·'
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <p className="account__name">{session.name}</p>
          <p className="muted" style={{ fontSize: 'var(--step--1)', wordBreak: 'break-word' }}>
            {session.email}
          </p>
        </div>
      </section>

      {/*
        * Participants only. Staff have no pass of their own to show - they are the ones
        * checking them, from the Passes tab.
        */}
      {!isStaff ? (
        <button className="account__pass" type="button" onClick={() => navigate('/pass')}>
          <span className="account__pass-icon" aria-hidden="true"><QrCode size={20} strokeWidth={1.5} /></span>
          <span className="account__pass-copy">
            <strong>E-Pass</strong>
            <span>Show this for attendance</span>
          </span>
          <ArrowRight size={17} strokeWidth={1.5} />
        </button>
      ) : null}

      <div className="rows">
        {session.track_name ? (
          <div className="row">
            {/*
              * No icon. A route glyph beside "Track" and an ID card beside "Role"
              * illustrated the labels rather than telling you anything, and "App
              * version" below has no equivalent - so its label started 29px left of
              * the other two and the column never lined up.
              */}
            <span className="row__label">Track</span>
            <span className="row__value">{session.track_name}</span>
          </div>
        ) : null}

        <div className="row">
          <span className="row__label">Role</span>
          {/*
            * The role's own name. This said "Venue staff" for every non-participant,
            * so an organiser and a projector account read identically - on the one
            * screen where someone checks which account they are signed in as.
            */}
          <span className="row__value">{ROLE_NAMES[Number(session.id_role)] ?? 'Staff'}</span>
        </div>

        <div className="row">
          <span className="row__label">App version</span>
          <span className="row__value tnum" title={`Build ${APP_BUILD}`}>
            {APP_LABEL}
          </span>
        </div>
      </div>

      {!isStaff ? (
        <section className="account__profile">
          <div className="account__profile-heading">
            {/*
              * One label, not two. "Event profile" sat directly above "Certificate
              * details" - a label for a label, over a single sentence of copy that
              * explains the section better than either of them.
              */}
            <div>
              <h2 className="account__profile-title">Certificate details</h2>
            </div>
            <Button
              variant="outline"
              size="sm"
              busy={profileLoading}
              disabled={Boolean(profile?.name_locked)}
              icon={<Pencil size={15} strokeWidth={1.5} />}
              onClick={() => void openProfileEditor()}
            >
              {editingProfile ? 'Close' : 'Edit'}
            </Button>
          </div>
          <p className="muted account__profile-copy">
            Update the name printed on your certificate. Your track was set when you
            first signed in and is fixed for the event.
          </p>
          {profileError ? <p className="field__error" role="alert">{profileError}</p> : null}
          {editingProfile && profile ? (
            <ParticipantProfileForm
              profile={profile}
              submitLabel="Save name"
              lockTrack
              onSaved={(result: SaveProfileResponse) => setProfile((current) => current ? {
                ...current,
                full_name: result.full_name,
                verified_name: result.verified_name,
                track_id: result.track_id,
                track_name: result.track_name,
              } : current)}
            />
          ) : null}
        </section>
      ) : null}

      {/*
        * Developer footer, dev builds only. Which API the app is pointed at matters when
        * telling a dev build from a production one; it means nothing to a delegate, and
        * the build line underneath it repeated what the App version row above already
        * carries in its tooltip. Support can still ask for the version from that row.
        */}
      {import.meta.env.DEV ? (
        <>
          <p className="faint" style={{ fontSize: 'var(--step--2)', paddingTop: 'var(--space-md)', wordBreak: 'break-all' }}>
            {API_BASE}
          </p>
          <p className="faint tnum" style={{ fontSize: 'var(--step--2)', paddingTop: 'var(--space-3xs)' }}>
            Build {APP_BUILD}
          </p>
        </>
      ) : null}

      <div style={{ paddingTop: 'var(--space-lg)' }}>
        <Button
          variant="outline"
          icon={<LogOut size={17} strokeWidth={1.5} />}
          onClick={() => setConfirming(true)}
        >
          Sign out
        </Button>
      </div>

      <Sheet open={confirming} onClose={() => setConfirming(false)} title="Sign out">
        <h2 className="detail__title">Sign out of this device?</h2>
        <p className="muted" style={{ marginTop: 'var(--space-xs)', fontSize: 'var(--step--1)' }}>
          Your attendance record stays safe. You will need to sign in again to check in
          at the next session.
        </p>
        <div style={{ display: 'grid', gap: 'var(--space-xs)', marginTop: 'var(--space-lg)' }}>
          <Button variant="primary" busy={busy} onClick={() => void leave()}>
            Sign out
          </Button>
          <Button variant="quiet" onClick={() => setConfirming(false)} style={{ margin: '0 auto' }}>
            Stay signed in
          </Button>
        </div>
      </Sheet>
    </main>
  )
}
